import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Collapse, Fade, Grid, Paper } from '@material-ui/core'
import clsx from 'clsx'
import moment from 'moment'
import get from 'lodash/get'
import last from 'lodash/last'

import { useSelector } from 'react-redux'
import { useAppDispatch } from 'planner-upgrades/redux/hooks'
import { ShipmentTour } from 'common/models/typings/Tour'
import { isValidCollation } from 'actions/tours/helpers'
import { changeCollationPosition, moveSingle } from 'actions/tours'
import { getLastStopTime, isPastDeadLine } from 'utils'

import Typography from 'common/components/base/Typography'
import Icon from 'common/components/base/Icon'
import Callout from 'common/components/base/.raw/Callout'
import AutoComplete from './AutoComplete'
import Actions from './Actions'
import SortableList from './SortableItem'

import useStyles from './useStyles'
import { NETWORK_TYPE } from '../../../models/typings/TourOverview'
import * as fromNetworkType from '../../../../selectors/networkType'
import { calculateTotalHoursOfTour } from './calculateTotalHoursOfTour'
import { Tooltip } from 'common/components/base/.raw'

import * as fromGreenRegions from '../../../../selectors/greenRegions'
import { getShipmentTours } from '../../../../selectors/combined'
import { Collation } from 'common/models/typings/Collation'
import { getSelectedTourId } from 'selectors/tour'

export interface TourCardProps {
  id: ShipmentTour['id']
  color: ShipmentTour['color']
  collations: ShipmentTour['collations']
  hasBermudaStop: ShipmentTour['hasBermudaStop']
  number: ShipmentTour['number']
  totalDistance: ShipmentTour['totalDistance']
  isWorking: ShipmentTour['isWorking']
  driverId: ShipmentTour['driverId']
  vehicleType: ShipmentTour['vehicleType']
  totalDuration: ShipmentTour['totalDuration']
}

const TourCard: React.FC<TourCardProps> = ({
  id,
  color,
  collations,
  hasBermudaStop,
  number,
  totalDistance,
  totalDuration,
  isWorking,
  driverId,
  vehicleType,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const networkType: NETWORK_TYPE = useSelector(fromNetworkType.getNetworkType)
  const allTours = useSelector(getShipmentTours)
  const selectedTourId: ShipmentTour['id'] | null =
    useSelector(getSelectedTourId)

  const greenRegions = useSelector(fromGreenRegions.getGreenRegions) as {
    [key: string]: { id: number; pc3: number; polygon: any }
  }

  const isSelected = React.useMemo(() => {
    return String(selectedTourId) === String(id)
  }, [selectedTourId, id])

  const {
    startTime,
    lastShipmentStopTime,
    endTime,
    pastDeadline,
    totalShipments,
    totalStops,
  } = React.useMemo(() => {
    return {
      startTime: moment(get(collations, '[0].estimation')).format('HH:mm'),
      lastShipmentStopTime: moment(getLastStopTime(collations)).format('HH:mm'),
      endTime: moment(get(last(collations), 'estimation')).format('HH:mm'),
      pastDeadline: isPastDeadLine(collations, networkType),
      totalShipments: collations
        .filter(isValidCollation as any)
        .reduce((a: number, b) => a + b.shipmentIds.length || a, 0),
      totalStops: collations.length - 2,
    }
  }, [collations])

  const totalDistanceInKM = React.useMemo(
    () => (totalDistance / 1000).toFixed(1).concat(' km'),
    [totalDistance],
  )

  const handleChangeCollationPosition = React.useCallback(
    (sort: { oldIndex: number; newIndex: number }) => {
      dispatch(changeCollationPosition(id, sort.oldIndex, sort.newIndex))
    },
    [dispatch, id],
  )

  const handleMoveTour = React.useCallback(
    (orgTour: ShipmentTour, collation: Collation, targetTour: ShipmentTour) => {
      dispatch(moveSingle(orgTour.id, targetTour.id, [collation.id]))
    },
    [dispatch],
  )

  const currentTour = React.useMemo(
    () => allTours.find((tour: ShipmentTour) => tour.id === id),
    [allTours, id],
  )

  const amountOfStops = collations.length

  const totalHoursOfTour = calculateTotalHoursOfTour(startTime, endTime)

  const lastStopTime = React.useMemo(() => {
    return moment(getLastStopTime(collations)).format('HH:mm')
  }, [collations])

  const productivity = React.useMemo(() => {
    if (!totalHoursOfTour || totalHoursOfTour === 0) return '-'
    const result = amountOfStops / totalHoursOfTour
    if (!Number.isFinite(result)) return '-'
    if (result < 0) return '-'
    return result.toFixed(1)
  }, [amountOfStops, totalHoursOfTour])

  const isWithinGreenRegion = React.useMemo(() => {
    if (greenRegions && collations) {
      const pc3s = Object.values(greenRegions).map((region) =>
        String(region.pc3),
      )
      return collations.some((collation) =>
        pc3s.includes(
          get(collation, 'recipient.location.postalCode', '').substring(0, 3),
        ),
      )
    }
    return false
  }, [greenRegions, collations])

  return (
    <>
      <div className="flex w-full flex-col bg-white rounded-lg p-4 mb-2 shadow-elevation-1">
        <div className="flex w-full flex-col justify-between">
          <div>
            <div className="flex flex-row w-full justify-between">
              <div className="flex flex-row items-center">
                <div
                  className={clsx('w-3 h-3 rounded-full mr-1')}
                  style={{ backgroundColor: color }}
                />
                <div className="text-sm font-bold text-indigo-50 text-nowrap">{`Tour ${number}`}</div>
                <Fade in={hasBermudaStop && !isSelected}>
                  <div className="ml-2 h-6">
                    <Icon name="danger" fill="#FF5351" />
                  </div>
                </Fade>
              </div>

              <div className="flex flex-row w-full">
                <Actions
                  id={id}
                  color={color}
                  hasBermudaStop={hasBermudaStop}
                  isSelected={isSelected}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="flex flex-row gap-2 mt-2 items-center">
              <div className="w-full">
                <div className="flex flex-row justify-start gap-4 items-center mr-4">
                  <div className="flex flex-col items-center">
                    <Tooltip text={t('START')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon name="start" className="w-4 h-4" />
                        <Typography noWrap className={classes.label}>
                          {startTime}
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                  <div className={clsx('flex flex-col items-center')}>
                    <Tooltip text={t('LAST_STOP')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon
                          name="pin"
                          className={clsx('w-4 h-4', {
                            'text-red-50': pastDeadline,
                          })}
                        />
                        <Typography
                          noWrap
                          className={clsx(classes.label, {
                            [classes.labelRed]: pastDeadline,
                          })}
                        >
                          {lastStopTime}
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                  <div className={clsx('flex flex-col items-center')}>
                    <Tooltip text={t('END')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon
                          name="end"
                          className={clsx('w-4 h-4', {
                            'text-red-50': pastDeadline,
                          })}
                        />
                        <Typography
                          noWrap
                          className={clsx(classes.label, {
                            [classes.labelRed]: pastDeadline,
                          })}
                        >
                          {endTime}
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex flex-col items-center">
                    <Tooltip text={t('SHIPMENTS_STOPS')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon name="package" className="w-4 h-4" />
                        <Typography noWrap className={classes.label}>
                          {totalShipments}/{totalStops}
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex flex-col items-center">
                    <Tooltip text={t('TOTAL_DISTANCE')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon name="ruler-dimension-line" className="w-4 h-4" />
                        <Typography noWrap className={classes.label}>
                          {parseFloat(totalDistanceInKM).toFixed(0)}km
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex flex-col items-center">
                    <Tooltip text={t('PRODUCTIVITY')} placement="top">
                      <div className="flex flex-col items-center">
                        <Icon name="target" className="w-4 h-4" />
                        <Typography noWrap className={classes.label}>
                          {productivity}
                        </Typography>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="w-full mr-2">
                <AutoComplete
                  id={id}
                  value={driverId}
                  vehicleType={vehicleType}
                  isWorking={isWorking}
                  isGreenRegion={isWithinGreenRegion}
                />
              </div>
            </div>
          </div>
        </div>
        <Collapse in={isSelected} mountOnEnter>
          <div id={`collapse-container-${id}`}>
            {hasBermudaStop && (
              <Box marginTop={2}>
                <Callout variant="danger">{t('BERMUDA_TEXT_CALLOUT')}</Callout>
              </Box>
            )}
            <div>
              {currentTour && (
                <SortableList
                  tour={currentTour}
                  tours={allTours}
                  locked={isWorking}
                  onMoveTour={handleMoveTour}
                  onSortEnd={handleChangeCollationPosition}
                />
              )}
            </div>
          </div>
        </Collapse>
      </div>
    </>
  )
}

export default React.memo(TourCard)
